import { Link } from "react-router-dom";
import { useState } from "react";
import { Linkedin, Twitter, Instagram, Facebook, MapPin, Phone, Mail, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { submitNewsletter } from "../lib/api";
import { SERVICES } from "../lib/services";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      setBusy(true);
      await submitNewsletter(email);
      toast.success("Subscribed", {
        description: "You'll receive our quarterly insights on wealth management.",
      });
      setEmail("");
    } catch (err) {
      toast.error("Subscription failed", { description: "Please try again." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#2A0A0E] text-[#FBF6EC] mt-32"
    >
      {/* Top thin maroon line */}
      <div className="gold-line" />

      {/* Newsletter strip */}
      <div className="container-myc py-16 grid lg:grid-cols-2 gap-10 border-b border-[#FBF6EC]/10">
        <div>
          <p className="text-[#C9A961] text-[11px] font-medium tracking-[0.25em] uppercase">
            Insights, quarterly
          </p>
          <h3 className="font-display text-2xl sm:text-3xl mt-4 text-[#FBF6EC] max-w-xl leading-tight">
            A measured perspective on markets, taxation, and family wealth — straight to your inbox.
          </h3>
        </div>
        <form
          onSubmit={onSubscribe}
          data-testid="newsletter-form"
          className="self-end flex flex-col sm:flex-row gap-3 w-full"
        >
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            data-testid="newsletter-email-input"
            className="flex-1 bg-transparent border border-[#FBF6EC]/20 px-5 py-4 text-[#FBF6EC] placeholder-[#FBF6EC]/40 focus:outline-none focus:border-[#C9A961] transition-colors"
          />
          <button
            type="submit"
            disabled={busy}
            data-testid="newsletter-submit-button"
            className="inline-flex items-center justify-center gap-2 bg-[#C9A961] text-[#2A0A0E] hover:bg-[#FBF6EC] transition-all duration-300 px-7 py-4 font-medium text-sm tracking-wider uppercase disabled:opacity-60"
          >
            {busy ? "Subscribing..." : "Subscribe"}
          </button>
        </form>
      </div>

      {/* Main grid */}
      <div className="container-myc py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="flex flex-col items-center leading-none w-fit" data-testid="footer-logo">
            <div className="flex items-baseline">
              <span className="font-display text-[40px] tracking-[-0.02em] font-bold text-[#FBF6EC]">
                MONEY
              </span>
              <span className="font-display text-[40px] tracking-[-0.02em] font-bold text-[#C9A961]">
                YARD
              </span>
            </div>
            <div className="w-full h-[1px] bg-[#C9A961] my-1.5" />
            <div className="flex flex-col items-center">
              <span className="font-display text-[13px] tracking-[0.55em] uppercase text-[#FBF6EC]/95 font-medium">
                Capital
              </span>
              <span className="font-display italic text-[12px] text-[#C9A961] mt-1 tracking-wide">
                Architects of Capital
              </span>
            </div>
          </div>
          <p className="mt-6 text-[#FBF6EC]/75 leading-relaxed max-w-md text-[15px]">
            An independent wealth advisory partnering with HNIs, NRIs and
            corporate clients to build, preserve, and transition capital across
            generations.
          </p>

          <div className="mt-8 space-y-4 text-sm">
            <a
              href="tel:+919892759155"
              data-testid="footer-phone-link"
              className="flex items-start gap-3 text-[#FBF6EC]/75 hover:text-[#C9A961] transition-colors"
            >
              <Phone className="w-4 h-4 mt-0.5 text-[#C9A961]" />
              +91 98927 59155
            </a>
            <a
              href="mailto:connect@moneyyardcapital.com"
              data-testid="footer-email-link"
              className="flex items-start gap-3 text-[#FBF6EC]/75 hover:text-[#C9A961] transition-colors"
            >
              <Mail className="w-4 h-4 mt-0.5 text-[#C9A961]" />
              connect@moneyyardcapital.com
            </a>
            <p className="flex items-start gap-3 text-[#FBF6EC]/75">
              <MapPin className="w-4 h-4 mt-0.5 text-[#C9A961] shrink-0" />
              <span>
                C-20, Enam Sambhav, G Block,<br />
                Bandra Kurla Complex, Bandra (East)<br />
                Mumbai — 400051, India
              </span>
            </p>
          </div>
        </div>

        <div className="lg:col-span-3">
          <p className="text-[#C9A961] text-[11px] font-medium tracking-[0.25em] uppercase mb-5">Company</p>
          <ul className="space-y-3 text-sm">
            {[
              { to: "/about", label: "About Us" },
              { to: "/services", label: "Services" },
              { to: "/nri-services", label: "NRI Services" },
              { to: "/insights", label: "Insights" },
              { to: "/contact", label: "Contact" },
            ].map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={`footer-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="text-[#FBF6EC]/75 hover:text-[#C9A961] transition-colors inline-flex items-center gap-1.5 group"
                >
                  {l.label}
                  <ArrowUpRight className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-3">
          <p className="text-[#C9A961] text-[11px] font-medium tracking-[0.25em] uppercase mb-5">Services</p>
          <ul className="space-y-3 text-sm">
            {SERVICES.slice(0, 8).map((s) => (
              <li key={s.slug}>
                <Link
                  to={`/services/${s.slug}`}
                  className="text-[#FBF6EC]/75 hover:text-[#C9A961] transition-colors"
                >
                  {s.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-2">
          <p className="text-[#C9A961] text-[11px] font-medium tracking-[0.25em] uppercase mb-5">Follow</p>
          <div className="flex gap-3">
            {[
              { icon: Linkedin, href: "#", label: "LinkedIn" },
              { icon: Twitter, href: "#", label: "Twitter" },
              { icon: Instagram, href: "#", label: "Instagram" },
              { icon: Facebook, href: "#", label: "Facebook" },
            ].map(({ icon: Icon, href, label }) => (
              <a
                key={label}
                href={href}
                aria-label={label}
                data-testid={`social-${label.toLowerCase()}`}
                className="w-10 h-10 inline-flex items-center justify-center border border-[#FBF6EC]/20 text-[#FBF6EC]/75 hover:text-[#C9A961] hover:border-[#C9A961] transition-colors"
              >
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="border-t border-[#FBF6EC]/10">
        <div className="container-myc py-8 text-xs text-[#FBF6EC]/55 leading-relaxed">
          <p className="mb-4">
            <span className="text-[#C9A961] font-semibold tracking-widest text-[10px] uppercase">
              Disclaimer:
            </span>{" "}
            Investments in securities and financial products are subject to
            market risks. Read all scheme-related documents carefully before
            investing. Past performance is not indicative of future returns.
            Moneyyard Capital does not guarantee any specific outcome or profit.
            Information on this website is for general information only and
            should not be construed as investment advice. Please consult your
            financial advisor before making any investment decision.
          </p>
          <div className="flex flex-col sm:flex-row justify-between gap-2">
            <p>© {new Date().getFullYear()} Moneyyard Capital. All rights reserved.</p>
            <p className="text-[#FBF6EC]/40">
              Crafted for the long-term investor.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
